import * as React from 'react'

import { AttendanceEmployeeRoutesDocument, EmployeeNameDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { MetaContext, MetaValue } from 'modules/core/lib/navigation/routes'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

import { AttendanceDateNavigatorProvider } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { AttendanceFeaturesProvider } from 'modules/attendance/lib/attendanceFeaturesContext'

import { buildTimesheetMeta } from './buildTimesheetMeta'

const AdminRootPage = React.lazy(() => import('modules/attendance/pages/AdminRootPage'))
const CustomPeriodsPage = React.lazy(() => import('modules/attendance/pages/CustomPeriodsPage'))
const EditOvertimeRequestPage = React.lazy(
  () => import('modules/attendance/pages/EditOvertimeRequestPage')
)
const EmployeeExportsModalPage = React.lazy(
  () => import('modules/attendance/pages/EmployeeExportsModalPage')
)

const EmployeePeriodShiftsPage = React.lazy(
  () => import('modules/attendance/pages/EmployeePeriodShiftsPage')
)
const EmployeeRootPage = React.lazy(() => import('modules/attendance/pages/EmployeeRootPage'))
const ExcludePeriodsModalPage = React.lazy(
  () => import('modules/attendance/pages/ExcludePeriodsModalPage')
)
const ExportYearlyBalanceModalPage = React.lazy(
  () => import('modules/attendance/pages/ExportYearlyBalanceModalPage')
)
const ExportsPeriodsModalPage = React.lazy(
  () => import('modules/attendance/pages/ExportsPeriodsModalPage')
)
const NavigateToCustomPeriodExportsPage = React.lazy(
  () => import('modules/attendance/pages/NavigateToCustomPeriodExportsPage')
)
const NavigateToCustomPeriodIndexPage = React.lazy(
  () => import('modules/attendance/pages/NavigateToCustomPeriodIndexPage')
)
const NewOvertimeRequestPage = React.lazy(
  () => import('modules/attendance/pages/NewOvertimeRequestPage')
)

const BreakdownSidepanelPage = React.lazy(() => import('modules/attendance/pages/SidepanelPage'))

const MyProjectsCustomPeriodsPage = React.lazy(
  () => import('modules/project_management/pages/MyProjectsCustomPeriodsPage')
)
const MyProjectsManagerRootPage = React.lazy(
  () => import('modules/project_management/pages/MyProjectsManagerRootPage')
)

const fetchEmployeeName = (
  { watchQuery, params }: MetaContext,
  origin?: string
): MetaValue<{ title: string; description: string }> => {
  const employeeId = Number(params.employeeId)

  return watchQuery({
    query: EmployeeNameDocument,
    variables: {
      employeeId,
    },
  }).map(({ data }) => {
    const employee = data.employees.employee
    const employeeName = employee?.access?.fullName || ''

    if (origin === 'myProjects') {
      return {
        title: i18n.t('project_management.meta_data.employee_projects_page.title', {
          employee_name: employeeName,
        }),
        description: i18n.t('project_management.meta_data.employee_projects_page.description', {
          employee_name: employeeName,
        }),
      }
    }

    return {
      title: employeeName,
      description: i18n.t('time_tracking.html_meta.timesheet'),
    }
  })
}

export const timeTrackingRoutes = buildNavigation((navigation) => [
  buildModuleRoute({
    path: navigation.timeTracking.path,
    handle: {
      module: 'time-tracking',
      moduleName: () => i18n.t('time_tracking.header.title'),
      meta: ({ watchQuery, session }) => {
        const metaObject = {
          title: i18n.t('time_tracking.header.title'),
          description: i18n.t('time_tracking.html_meta.page'),
        }

        if (!session.employee) return metaObject

        return watchQuery({
          query: AttendanceEmployeeRoutesDocument,
          variables: {
            employeeId: session.employee.id,
            skipFetchingSettings: true,
          },
        }).map(({ data }) => {
          const settings = data.attendance._policies.accessEditSettings.can
            ? navigation.settings.time_tracking.fullPath()
            : undefined

          return {
            ...metaObject,
            settings,
          }
        })
      },
    },
    element: (
      <AttendanceFeaturesProvider>
        <AttendanceDateNavigatorProvider>
          <AdminRootPage />
        </AttendanceDateNavigatorProvider>
      </AttendanceFeaturesProvider>
    ),
    children: [
      {
        index: true,
        element: <NavigateToCustomPeriodIndexPage />,
      },
      {
        path: navigation.timeTracking.list.path,
        children: [
          {
            index: true,
            element: <NavigateToCustomPeriodIndexPage />,
          },
          {
            path: navigation.timeTracking.list.yearMonth.list.path,
            children: [
              {
                index: true,
                element: <NavigateToCustomPeriodIndexPage />,
              }, // dependency from backend Onboarding task
              {
                path: navigation.timeTracking.list.yearMonth.list.exports.path,
                element: <NavigateToCustomPeriodExportsPage />,
              },
            ],
          },
          {
            path: navigation.timeTracking.list.periodTypeYearMonthDay.path,
            element: <CustomPeriodsPage />,

            children: [
              {
                path: navigation.timeTracking.list.periodTypeYearMonthDay.exports.path,
                element: <ExportsPeriodsModalPage />,
              },
              {
                path: navigation.timeTracking.list.periodTypeYearMonthDay.exportYearlyBalance.path,
                element: <ExportYearlyBalanceModalPage />,
              },
              {
                path: navigation.timeTracking.list.periodTypeYearMonthDay.exclude.path,
                element: <ExcludePeriodsModalPage />,
              },
            ],
          },
        ],
      },
      {
        path: navigation.timeTracking.employee.path,
        element: <EmployeeRootPage />,
        children: [
          {
            path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.path,
            element: <EmployeePeriodShiftsPage />,
            handle: {
              resource: true,
              meta: (context) => {
                return buildTimesheetMeta(context, navigation)
              },
            },
            children: [
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.exports
                  .path,
                element: <EmployeeExportsModalPage />,
              },
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
                  .requestOvertime.path,
                element: <NewOvertimeRequestPage />,
              },
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
                  .overtimeRequest.path,
                element: <EditOvertimeRequestPage />,
              },
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
                  .workedHoursBreakdownEmployeeId.path,
                element: <BreakdownSidepanelPage tabFromNavigation="worked_hours" />,
              },
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
                  .balanceBreakdownEmployeeId.path,
                element: <BreakdownSidepanelPage tabFromNavigation="balance" />,
              },
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
                  .compensationBreakdownEmployeeId.path,
                element: <BreakdownSidepanelPage tabFromNavigation="compensation" />,
              },
              {
                path: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
                  .bankOfHoursEmployeeId.path,
                element: <BreakdownSidepanelPage tabFromNavigation="bank_of_hours" />,
              },
            ],
          },
          {
            path: navigation.timeTracking.employee.myProjects.path,
            element: <MyProjectsManagerRootPage />,
            children: [
              {
                path: navigation.timeTracking.employee.myProjects.yearMonthDayEmployeeId.path,
                element: <MyProjectsCustomPeriodsPage />,
                handle: {
                  meta: (context) => fetchEmployeeName(context, 'myProjects'),
                },
              },
            ],
          },
        ],
      },
    ],
  }),
])
